// What each feed is, and who it comes from.
//
// Editorial copy, so it lives in the frontend rather than on the wire: it never
// changes between requests, it would be dead weight in every /diagnostics
// response, and a sentence explaining a data source is not something the server
// should be in the business of holding.
//
// Keyed by the feed id the backend reports. Anything unlisted still renders —
// with its label, upstream and licence from the API — just without a sentence,
// so adding a provider never leaves a hole here that has to be filled before
// the page works.

export interface SourceNote {
  /** One sentence: what this is, in the reader's terms, not ours. */
  readonly description: string;
  /** Where a reader goes to check us, or to read the source themselves. */
  readonly url?: string;
  /** Shown when the reader should know the data is derived, not observed. */
  readonly caveat?: string;
}

export const SOURCE_NOTES: Record<string, SourceNote> = {
  aircraft: {
    description:
      'Every aircraft broadcasting ADS-B within about fifty nautical miles of the city, '
      + 'picked up by a volunteer network of ground receivers.',
    url: 'https://adsb.lol/',
    caveat: 'Aircraft that do not transmit, or that transmit without a position, do not appear.',
  },
  buses: {
    description:
      "Ember's electric coaches, live from the operator's own realtime feed.",
    url: 'https://www.ember.to/',
    caveat: 'Only Ember. Other operators do not publish live positions for this area.',
  },
  vessels: {
    description:
      "Ships in each city's own water — Aberdeen harbour and the sea off it, the Firth of "
      + 'Forth, and the Clyde down to the Tail of the Bank — from AIS transmissions relayed '
      + 'by aisstream.io.',
    url: 'https://aisstream.io/',
    caveat: 'A berthed ship can be quiet for minutes at a time, so a harbour fills in slowly.',
  },
  ferries: {
    description:
      'NorthLink sailings to Orkney and Shetland, identified from the same AIS stream as other shipping.',
    url: 'https://www.northlinkferries.co.uk/',
  },
  rail: {
    description:
      "Arrival and departure boards for the city's stations and the stops around it, from "
      + "National Rail's Darwin service.",
    url: 'https://raildata.org.uk/',
  },
  'rail-positions': {
    description:
      'Where a train probably is between stations, worked out from its Darwin timings and '
      + 'the track geometry.',
    url: 'https://www.openstreetmap.org/copyright',
    caveat:
      'Estimated, not observed. Britain has no public live train-position feed, so these are '
      + 'interpolated and labelled as such everywhere they appear.',
  },
  'bus-stops': {
    description:
      'Every bus stop in the area and its scheduled departures, from the national timetable dataset.',
    url: 'https://www.bus-data.dft.gov.uk/',
    caveat: 'The published timetable, not live running. Refreshed weekly.',
  },
  airport: {
    description:
      "Movements at the city's airport, worked out from the ADS-B feed rather than from an "
      + 'airport source.',
    caveat: 'Derived from aircraft transmissions, so it is not the airport’s own board.',
  },
  voi: {
    description: 'Voi e-bikes and scooters available to hire.',
    url: 'https://www.voi.com/',
  },
  // Two entries because the sources page credits each wires company its own
  // card; the map still draws both through the one power layer.
  power: {
    description:
      'Live and planned power cuts on the network SSEN runs — the north of '
      + 'Scotland, including Aberdeen. Published under CC BY 4.0.',
    url: 'https://www.ssen.co.uk/power-track/',
  },
  'power-spen': {
    description:
      'Live and planned power cuts on the SP Energy Networks patch — central '
      + 'and southern Scotland, including Edinburgh and Glasgow. Published '
      + 'under CC BY 4.0.',
    url: 'https://www.spenergynetworks.co.uk/pages/power_cuts.aspx',
    caveat:
      "SP Energy Networks' feed omits incidents affecting fewer than five "
      + 'customers for privacy, so its count reads slightly low against their own outage map.',
  },
  fubar: {
    description:
      'Local reporting posted by FUBAR News. Editorial content chosen by their newsroom, '
      + 'not an automated feed.',
  },
  water: {
    description: 'Supply interruptions from Scottish Water.',
    url: 'https://www.scottishwater.co.uk/',
    caveat: 'Scottish Water publish interruptions on their website but offer no open feed, so this layer is empty.',
  },
  telecoms: {
    description: 'Broadband and mobile outages.',
    caveat: 'No UK operator publishes an open outage feed, so this layer is empty.',
  },
  warnings: {
    description:
      'Severe weather warnings the Met Office have issued — wind, rain, snow, thunderstorms '
      + 'and the rest — in the Now box rather than behind a toggle, and drawn over the area '
      + 'each one actually covers. Data supplied by the Met Office.',
    url: 'https://www.metoffice.gov.uk/weather/warnings-and-advice/uk-warnings',
    caveat:
      'A warning covers a whole region, so one can be in force for the area without reaching '
      + 'your street. Yellow means be aware, amber be prepared, red take action.',
  },
  // Two entries since the incidents/works split: they are different news —
  // a crash is an event, month four of a bypass scheme is a condition of the
  // road — and each card should say which it is describing.
  incidents: {
    description:
      'Crashes, closures and breakdowns happening now on the trunk road network, '
      + 'from Traffic Scotland’s DATEX II feed. © Transport Scotland, who own the data and '
      + 'all rights in it.',
    url: 'https://www.traffic.gov.scot/',
    caveat:
      'Trunk roads only — the A90, A96, A9 and similar — so a closure on a council-managed '
      + 'city street will not appear. Supplied without warranty as to accuracy or '
      + 'availability, and this site is not connected with Transport Scotland.',
  },
  roadworks: {
    description:
      'Roadworks in progress on the trunk road network, from Traffic Scotland’s DATEX II '
      + 'feed. © Transport Scotland, who own the data and all rights in it.',
    url: 'https://www.traffic.gov.scot/',
    caveat:
      'Many schemes run for months, so the Now box only calls out works that began within '
      + 'the last five days — the rest stay on the map for anyone who asks. Works scheduled '
      + 'for a future date are a separate layer. Supplied without warranty as to accuracy '
      + 'or availability, and this site is not connected with Transport Scotland.',
  },
  'roadworks-future': {
    description:
      'Roadworks scheduled for a future date on the trunk road network, from Traffic '
      + 'Scotland’s DATEX II feed. © Transport Scotland.',
    url: 'https://www.traffic.gov.scot/',
    caveat:
      'Not happening yet. These are works with a start date ahead of today, kept out of the '
      + 'live layer so that nothing on the map claims a road is disrupted before it is. '
      + 'Dates can move — the layer shows what is currently planned, not a promise.',
  },
  'local-roadworks': {
    description:
      'Road works on every road, council streets included, from the Scottish Road Works '
      + 'Register — the one register every Scottish roads authority and utility files into. '
      + 'Published by the Scottish Road Works Commissioner under the Open Government Licence.',
    url: 'https://roadworks.scot/publications/scottish-road-works-register-open-data',
    caveat:
      'The register publishes one file a day of what changed, so this is a picture built up '
      + 'day by day on our server rather than a live feed: a works is drawn while the register '
      + 'says it is in progress, and leaves when the register closes it. Only works the register '
      + 'gives an extent for are drawn; the rest are counted on the status page. Dates are '
      + 'what the promoter filed and can move.',
  },
  vms: {
    description:
      'What the roadside message signs are displaying, shown as the sign displays it — the '
      + 'operator’s own words and line breaks, not a summary. © Transport Scotland.',
    url: 'https://www.traffic.gov.scot/',
    caveat:
      'Sign locations come from a table baked from the same feed; messages are live. A sign '
      + 'showing nothing is not drawn, so an absence here means the gantries near you are '
      + 'blank rather than that the layer is broken.',
  },
  'journey-times': {
    description:
      'How long each instrumented stretch of trunk road is taking to drive right now, '
      + 'against how long it normally takes at this time of day. Measured by loops cut '
      + 'into the carriageway, from Traffic Scotland’s DATEX II feed. © Transport Scotland.',
    url: 'https://www.traffic.gov.scot/',
    caveat:
      'Green, amber and red are the delay against a normal journey, not against an empty '
      + 'road — so a busy but ordinary rush hour reads as green, which is what it is. The '
      + 'coloured road itself is drawn on OpenStreetMap geometry (© OpenStreetMap '
      + 'contributors, ODbL) matched to the section endpoints Traffic Scotland publish; '
      + 'sections we could not match to a road keep a marker and no colour. Only the '
      + 'instrumented stretches appear — most of the network is not measured at all.',
  },
  cameras: {
    description:
      'Traffic Scotland roadside camera images. © Transport Scotland.',
    url: 'https://www.traffic.gov.scot/',
    caveat:
      'A still, not a video feed, and refreshed on Traffic Scotland’s own schedule rather '
      + 'than ours.',
  },
  floods: {
    description:
      'Flood alerts and warnings SEPA have actually issued, in the Now box rather than '
      + 'behind a toggle — a red warning is not something anyone should have to opt into.',
    url: 'https://floodline.sepa.org.uk/',
    caveat:
      'SEPA issue these for whole areas, so a pin marks the city rather than the flooding, '
      + 'and an area not yet mapped to a city will not appear here.',
  },
  rivers: {
    description:
      "River levels at SEPA's gauging stations on the Dee, the Don and their tributaries, "
      + 'with the recent trend and how the level compares with a normal month.',
    url: 'https://timeseries.sepa.org.uk/',
  },
  fuel: {
    description:
      'Pump prices at every forecourt in the area, from the retailers’ own statutory filings.',
    url: 'https://www.gov.uk/guidance/fuel-finder-scheme',
    caveat: 'Retailers file prices on their own schedule, so a price can be a few hours behind the sign.',
  },
  weather: {
    description:
      'Temperature, wind, gusts and conditions — an hourly observation from the Met '
      + 'Office’s nearest station to the city, not a forecast.',
    url: 'https://www.metoffice.gov.uk/',
    caveat:
      'If the Met Office is unreachable the panel holds the last observation rather than '
      + 'substituting a forecast, so check the time it was taken. Sea state comes from a '
      + 'separate source and is reported on its own line below.',
  },
  // Two entries, because they are two feeds that fail independently. The sea
  // half can be live while the land half is down and the other way round, and a
  // reader checking whether something is broken needs to see which.
  sea: {
    description:
      'Significant wave height, period, direction and sea temperature from Cefas WaveNet — '
      + 'measured by instruments in the water, not modelled. Includes the offshore '
      + 'installations, which report wave height only.',
    url: 'https://wavenet.cefas.co.uk/',
    caveat:
      'Each station names its own provider: Cefas run the wave buoys, while the platform '
      + 'readings come from Oil and Gas UK and Shell UK and are relayed by Cefas. The nearest '
      + 'buoy can be tens of kilometres offshore and the nearest rig further still, so the '
      + 'weather panel names which station a reading came from rather than presenting it as '
      + 'the city’s own.',
  },
  waves: {
    description:
      'Every sea-state station within about 320 km — wave buoys and offshore oil and gas '
      + 'platforms — shown on the map with its latest reading.',
    url: 'https://wavenet.cefas.co.uk/',
    caveat:
      'Platforms report significant wave height only; the wave buoys also report period, '
      + 'direction, spread and sea temperature. Readings are half-hourly from the buoys and '
      + 'hourly from the platforms.',
  },
  tickets: {
    description:
      'Ticket links on the rail boards go to traintickets.com, run by Atomised Co-operative '
      + 'Ltd — a small Scottish worker co-operative that charges no booking fee and takes a '
      + 'share only of a split-ticket saving it finds you.',
    url: 'https://www.traintickets.com/',
    caveat:
      'An unpaid link. There is no affiliate arrangement, no commission and no tracking — '
      + 'this site earns nothing whether or not you buy. It is here because it is the most '
      + 'honest way to get a ticket for a train on these boards, not because it pays.',
  },
  festivals: {
    description:
      'Venues and what is on at each, for whichever of Edinburgh\'s festivals '
      + 'are running today — data provided courtesy of the Edinburgh Festivals '
      + 'Listings API. One layer per festival, and none for a festival that is '
      + 'not on.',
    url: 'https://api.edinburghfestivalcity.com/',
    caveat:
      'Not an official festival application. Cancellations reach this map on '
      + 'the hourly refresh, so a show cancelled in the last hour may still '
      + 'appear — check the festival\'s own site before travelling.',
  },
  lez: {
    description:
      'The Low Emission Zone boundary, from the national dataset all four '
      + 'Scottish LEZ cities publish through the Spatial Hub.',
    url: 'https://www.lowemissionzones.scot/',
    caveat:
      'The boundary only. Whether a particular vehicle may enter depends on its '
      + 'emissions standard, which the Scottish Government’s own checker answers '
      + '— the card links to it. Boundaries are reprojected from the National '
      + 'Grid and are accurate to a few metres, so do not use this to judge a '
      + 'single kerbside.',
  },
  air: {
    description:
      'Automatic air quality monitoring stations, from Defra\u2019s Automatic Urban '
      + 'and Rural Network (AURN) \u2014 where each station is, what it is currently '
      + 'measuring pollutant by pollutant, and its Daily Air Quality Index.',
    url: 'https://uk-air.defra.gov.uk/',
    caveat:
      'Provisional readings from the automatic network, published before quality '
      + 'assurance \u2014 a ratified figure released later may differ. The index uses '
      + 'the official Low to Very High thresholds on the averaging periods they '
      + 'specify; where a station cannot be banded honestly it shows readings and no '
      + 'band, and the card names which pollutants its index is built from. This is '
      + 'the AURN, one of several UK networks \u2014 locally-managed sites are a '
      + 'separate network and are not in this feed \u2014 and it is what each '
      + 'instrument measures now, not Defra\u2019s forecast for the days ahead.',
  },

  overflows: {
    description:
      'Monitored sewer overflows on the Scottish Water network, from their '
      + 'near real-time Event Duration Monitoring feed — where a monitor is, '
      + 'whether it is activated now, and how long the last activation lasted.',
    url: 'https://www.scottishwater.co.uk/Your-Home/Your-Waste-Water/Overflows/Live-Overflow-Map',
    caveat:
      'A monitor indicates an overflow; it does not confirm one, and Scottish '
      + 'Water note that errors happen. Nothing here is a statement about '
      + 'pollution or whether water is safe to swim in. Refer to Scottish '
      + 'Water\u2019s own map for the authoritative picture.',
  },
  taxis: {
    description:
      'Registered taxi ranks in the city and the towns around it, from NaPTAN — the '
      + 'Department for Transport’s national register of public transport access points.',
    url: 'https://naptan.api.dft.gov.uk',
    caveat:
      'Where a rank is, not whether a taxi is on it — no feed reports waiting cabs. Councils '
      + 'register ranks voluntarily, so a town with none listed may still have one.',
  },
};
